import { throttle } from 'es-toolkit';
import { useCallback, useEffect, useRef } from 'react';

class Timeline {
  circleElements: NodeListOf<HTMLElement>;
  index: number;
  circleChildElements: HTMLElement[];
  progressElement: HTMLElement;
  progressPercentage: number;
  timelineElement: HTMLElement;
  throttledEventHandler: (arg0: Event) => void;

  constructor(timelineElement: HTMLElement, index: number) {
    this.index = index;
    this.timelineElement = timelineElement;
    this.circleElements = timelineElement.querySelectorAll('.embedded-timeline__circle');
    this.circleChildElements = [];
    this.progressElement = timelineElement.querySelector('.embedded-timeline__progress');
    this.progressPercentage = 0;
    this.throttledEventHandler = throttle(this.handleScroll.bind(this), 100);
  }

  handleScroll() {
    const timelineElement = this.timelineElement;
    const progressElement = this.progressElement;

    if (!timelineElement || !progressElement) return;

    const calculateScrollProgress = () => {
      const { top, height } = timelineElement.getBoundingClientRect();
      const midViewport = window.innerHeight / 2;
      const distanceScrolled = midViewport - top;
      return Math.max(0, Math.min(1, distanceScrolled / height)) * 100;
    };

    this.progressPercentage = calculateScrollProgress();
    progressElement.style.transform = `translate3d(0, ${-100 + this.progressPercentage}%, 0)`;

    const updateIntersectingIndexes = () => {
      const progressRect = progressElement.getBoundingClientRect();
      this.circleElements.forEach((circle, index) => {
        if (!circle) return;
        if (!this.circleChildElements[index]) {
          this.circleChildElements[index] = circle.querySelector('div');
        }

        const circleRect = circle.getBoundingClientRect();
        const isIntersecting = progressRect.bottom >= circleRect.top;

        if (isIntersecting) {
          this.circleChildElements[index].classList.remove('bg-primary-pink');
          this.circleChildElements[index].classList.add('bg-primary-magenta');
        } else {
          this.circleChildElements[index].classList.remove('bg-primary-magenta');
          this.circleChildElements[index].classList.add('bg-primary-pink');
        }
      });
    };

    updateIntersectingIndexes();
  }

  attachScrollEvents() {
    window.addEventListener('scroll', this.throttledEventHandler, { passive: true });
  }

  removeEventListeners() {
    window.removeEventListener('scroll', this.throttledEventHandler, {
      passive: true,
    } as AddEventListenerOptions);
  }

  destroy() {
    this.removeEventListeners();
  }

  init() {
    this.attachScrollEvents();
  }
}

const useTimeline = () => {
  const timelineElementsRef = useRef<NodeListOf<HTMLElement> | null>();
  const timelinesRef = useRef<Timeline[] | null>([]);
  const intersectionRef = useRef<IntersectionObserver | null>(null);

  const handleTimelineIntersectionCheck = useCallback((entries, _observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        timelinesRef.current[entry.target.dataset.index].attachScrollEvents();
      } else {
        timelinesRef.current[entry.target.dataset.index].removeEventListeners();
      }
    });
  }, []);

  const initIntersections = useCallback(() => {
    const timelineElements = timelineElementsRef.current;

    timelineElements.forEach((timelineElement) => {
      const halfWindowHeight = `${window.innerHeight / 2}px`;
      const options = {
        rootMargin: `${halfWindowHeight} 0% ${halfWindowHeight} 0%`,
      };

      const observer = new IntersectionObserver(handleTimelineIntersectionCheck, options);

      observer.observe(timelineElement);
      intersectionRef.current = observer;
    });
  }, [handleTimelineIntersectionCheck, intersectionRef, timelineElementsRef]);

  useEffect(() => {
    const timelines = timelinesRef.current;

    timelineElementsRef.current = document.querySelectorAll('.embedded-timeline');

    timelineElementsRef.current.forEach((timelineElement, index) => {
      timelineElement.setAttribute('data-index', `${index}`);
      const timeline = new Timeline(timelineElement, index);
      timelines.push(timeline);
    });

    initIntersections();

    return () => {
      timelines.forEach((timeline) => timeline.destroy());
      timelineElementsRef.current.forEach((timelineElement) => {
        intersectionRef.current.unobserve(timelineElement);
      });
    };
  }, [initIntersections]);
};

export default useTimeline;
